/**
 * Symmetry plane visualization — renders semi-transparent planes at the
 * object's local origin for each enabled symmetry axis.
 *
 * Modular and reusable: accepts axes and size props, can be placed under
 * any object transform group.  Currently used by sculpt mode but designed
 * for future use in edit mode mirror operations.
 *
 * Each axis plane uses an industry-standard color coding:
 *   X → red, Y → green, Z → blue
 *
 * The plane renders with depth testing enabled but no depth writes, so it
 * appears as a subtle transparent overlay that correctly occludes with the
 * mesh surface.  A fine dashed center line along the intersection edge
 * provides a clean visual reference matching Blender's mirror axis display.
 */

import { useMemo } from "react";
import * as THREE from "three/webgpu";
import { useDispose } from "./hooks/useDispose.ts";

export type SymmetryAxis = "x" | "y" | "z";

interface SymmetryPlaneOverlayProps {
  /** Which axes to show planes for. */
  axes: SymmetryAxis[];
  /** World-space size of the plane (auto-sized to bounding box if omitted). */
  size?: number;
}

// Axis → rotation to align an XY plane with the symmetry plane
const AXIS_ROTATION: Record<SymmetryAxis, THREE.Euler> = {
  x: new THREE.Euler(0, Math.PI / 2, 0), // YZ plane (mirror across X)
  y: new THREE.Euler(Math.PI / 2, 0, 0), // XZ plane (mirror across Y)
  z: new THREE.Euler(0, 0, 0), // XY plane (mirror across Z)
};

const AXIS_COLOR: Record<SymmetryAxis, number> = {
  x: 0xe05555, // red
  y: 0x55b855, // green
  z: 0x5588dd, // blue
};

const PLANE_SEGMENTS = 1;

function SymmetryPlane({ axis, size }: { axis: SymmetryAxis; size: number }) {
  const planeGeo = useMemo(
    () => new THREE.PlaneGeometry(size, size, PLANE_SEGMENTS, PLANE_SEGMENTS),
    [size],
  );
  useDispose(planeGeo);

  const planeMat = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        color: AXIS_COLOR[axis],
        transparent: true,
        opacity: 0.06,
        side: THREE.DoubleSide,
        depthWrite: false,
        depthTest: true,
      }),
    [axis],
  );
  useDispose(planeMat);

  // Center line — a single line segment along the plane's intersection
  // with the mesh (horizontal for YZ plane, etc.)
  const lineGeo = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    const half = size / 2;
    // Line runs along the plane's local Y axis (vertical edge of the plane)
    geo.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(
        [-half, 0, 0, half, 0, 0, 0, -half, 0, 0, half, 0],
        3,
      ),
    );
    return geo;
  }, [size]);
  useDispose(lineGeo);

  const lineMat = useMemo(
    () =>
      new THREE.LineBasicMaterial({
        color: AXIS_COLOR[axis],
        transparent: true,
        opacity: 0.35,
        depthWrite: false,
        depthTest: true,
      }),
    [axis],
  );
  useDispose(lineMat);

  return (
    <group rotation={AXIS_ROTATION[axis]}>
      <mesh
        geometry={planeGeo}
        material={planeMat}
        renderOrder={1}
        raycast={() => {}}
      />
      <lineSegments
        geometry={lineGeo}
        material={lineMat}
        renderOrder={2}
        raycast={() => {}}
      />
    </group>
  );
}

export function SymmetryPlaneOverlay({
  axes,
  size = 4,
}: SymmetryPlaneOverlayProps) {
  if (axes.length === 0) return null;

  return (
    <group userData={{ excludeFromExport: true }}>
      {axes.map((axis) => (
        <SymmetryPlane key={axis} axis={axis} size={size} />
      ))}
    </group>
  );
}
